import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/img/logo.png";
import "./style.css";

export default function Navbar() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isIndustriesOpen, setIsIndustriesOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const servicesRef = useRef(null);
  const productsRef = useRef(null);
  const industriesRef = useRef(null);
  const aboutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
      if (isNavOpen) setIsNavOpen(false);
      setIsServicesOpen(false);
      setIsProductsOpen(false);
      setIsIndustriesOpen(false);
      setIsAboutOpen(false);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isNavOpen]);

  // Close dropdowns on outside click (desktop)
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
      if (productsRef.current && !productsRef.current.contains(e.target)) {
        setIsProductsOpen(false);
      }
      if (industriesRef.current && !industriesRef.current.contains(e.target)) {
        setIsIndustriesOpen(false);
      }
      if (aboutRef.current && !aboutRef.current.contains(e.target)) {
        setIsAboutOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const toggleNav = () => setIsNavOpen((prev) => !prev);
  const closeNav = () => {
    setIsNavOpen(false);
    setIsServicesOpen(false);
    setIsProductsOpen(false);
    setIsIndustriesOpen(false);
    setIsAboutOpen(false);
    navigate("/about/contact");
  };

  const toggleServices = (e) => {
    e.preventDefault();
    setIsServicesOpen((prev) => !prev);
    setIsProductsOpen(false);
    setIsIndustriesOpen(false);
    setIsAboutOpen(false);
  };

  const toggleProducts = (e) => {
    e.preventDefault();
    setIsProductsOpen((prev) => !prev);
    setIsServicesOpen(false);
    setIsIndustriesOpen(false);
    setIsAboutOpen(false);
  };

  const toggleIndustries = (e) => {
    e.preventDefault();
    setIsIndustriesOpen((prev) => !prev);
    setIsServicesOpen(false);
    setIsProductsOpen(false);
    setIsAboutOpen(false);
  };

  const toggleAbout = (e) => {
    e.preventDefault();
    setIsAboutOpen((prev) => !prev);
    setIsServicesOpen(false);
    setIsProductsOpen(false);
    setIsIndustriesOpen(false);
  };

  return (
    <nav
      className={`navbar navbar-expand-lg navbar-custom ${isScrolled ? "navbar-scrolled" : ""} ${isNavOpen ? "nav-open" : ""}`}
    >
      <div className="container nav-container">
        {/* Logo and Company Name */}
        <a className="navbar-brand" href="/" onClick={closeNav}>
          <div className="logo-icon">
            <img src={logo} alt="BCT" className="logo-img" />
          </div>
          <span className="brand-text">Best Code Technology</span>
        </a>

        {/* Hamburger Toggler */}
        <button
          className={`navbar-toggler ${isNavOpen ? "toggler-active" : ""}`}
          type="button"
          onClick={toggleNav}
          aria-expanded={isNavOpen}
          aria-label="Toggle navigation"
        >
          <span className="hamburger-bar"></span>
          <span className="hamburger-bar"></span>
          <span className="hamburger-bar"></span>
        </button>

        {/* Collapsible Content */}
        <div
          className={`navbar-collapse ${isNavOpen ? "show" : "collapse"}`}
          id="navbarNav"
        >
          {/* Center Navigation Links */}
          <ul className="navbar-nav nav-center">
            <li
              ref={servicesRef}
              className={`nav-item nav-item-dropdown px-1 ${isServicesOpen ? "dropdown-open" : ""}`}
              onMouseEnter={() => {
                if (window.innerWidth >= 992) setIsServicesOpen(true);
              }}
              onMouseLeave={() => {
                if (window.innerWidth >= 992) setIsServicesOpen(false);
              }}
            >
              <button
                className="nav-link nav-link-dropdown"
                onClick={toggleServices}
                aria-expanded={isServicesOpen}
                aria-haspopup="true"
              >
                Services
                <svg
                  className={`dropdown-chevron ${isServicesOpen ? "chevron-open" : ""}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 4L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <ul className="nav-submenu" role="menu">
                <li role="none">
                  <a
                    href="/services/ai-applications"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    AI Applications
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/services/web-applications"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    Web Application
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/services/mobile-applications"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    Mobile Application
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/services/cloud-deployment"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    Cloud Deployment
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/services/messagehub"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    MessageHub
                  </a>
                </li>
              </ul>
            </li>
            <li
              ref={productsRef}
              className={`nav-item nav-item-dropdown px-1 ${isProductsOpen ? "dropdown-open" : ""}`}
              onMouseEnter={() => {
                if (window.innerWidth >= 992) setIsProductsOpen(true);
              }}
              onMouseLeave={() => {
                if (window.innerWidth >= 992) setIsProductsOpen(false);
              }}
            >
              <button
                className="nav-link nav-link-dropdown"
                onClick={toggleProducts}
                aria-expanded={isProductsOpen}
                aria-haspopup="true"
              >
                Products
                <svg
                  className={`dropdown-chevron ${isProductsOpen ? "chevron-open" : ""}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 4L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <ul className="nav-submenu" role="menu">
                <li role="none">
                  <a
                    href="/products/unibuilder"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    UniBuilder
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/products/task-alarm"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    Task Alarm
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/products/ai-bridge"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    AI Bridge
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/products/knowledge-graph"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    Knowledge Graph
                  </a>
                </li>
              </ul>
            </li>
            <li
              ref={industriesRef}
              className={`nav-item nav-item-dropdown px-1 ${isIndustriesOpen ? "dropdown-open" : ""}`}
              onMouseEnter={() => {
                if (window.innerWidth >= 992) setIsIndustriesOpen(true);
              }}
              onMouseLeave={() => {
                if (window.innerWidth >= 992) setIsIndustriesOpen(false);
              }}
            >
              <button
                className="nav-link nav-link-dropdown"
                onClick={toggleIndustries}
                aria-expanded={isIndustriesOpen}
                aria-haspopup="true"
              >
                Industries
                <svg
                  className={`dropdown-chevron ${isIndustriesOpen ? "chevron-open" : ""}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 4L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <ul className="nav-submenu" role="menu">
                <li role="none">
                  <a
                    href="/industries/finance"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    Finance
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/industries/healthcare"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    Health Care
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/industries/education"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    Education
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/industries/logistics"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    Logistic
                  </a>
                </li>
                <li role="none">
                  <a
                    href="/industries/ai-products"
                    onClick={closeNav}
                    role="menuitem"
                  >
                    AI Products
                  </a>
                </li>
              </ul>
            </li>
            <li
              ref={aboutRef}
              className={`nav-item nav-item-dropdown px-1 ${isAboutOpen ? "dropdown-open" : ""}`}
              onMouseEnter={() => {
                if (window.innerWidth >= 992) setIsAboutOpen(true);
              }}
              onMouseLeave={() => {
                if (window.innerWidth >= 992) setIsAboutOpen(false);
              }}
            >
              <button
                className="nav-link nav-link-dropdown"
                onClick={toggleAbout}
                aria-expanded={isAboutOpen}
                aria-haspopup="true"
              >
                About
                <svg
                  className={`dropdown-chevron ${isAboutOpen ? "chevron-open" : ""}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 4L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <ul className="nav-submenu" role="menu">
                <li role="none">
                  <a href="/about/company" onClick={closeNav} role="menuitem">
                    Company
                  </a>
                </li>
                <li role="none">
                  <a href="/about/career" onClick={closeNav} role="menuitem">
                    Career
                  </a>
                </li>
                <li role="none">
                  <a href="/about/contact" onClick={closeNav} role="menuitem">
                    Contact
                  </a>
                </li>
              </ul>
            </li>
            {/* <li className="nav-item">
              <a className="nav-link" href="#support" onClick={closeNav}>
                Support
              </a>
            </li> */}
          </ul>

          {/* Right Section: Log in + CTA Button */}
          <div className="nav-right">
            {/* <a
              className="nav-login"
              href="https://unibuilder.in/login"
              onClick={closeNav}
            >
              Log in
            </a> */}
            <button className="btn btn-primary-custom" onClick={closeNav}>
              Book a Consultation
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
